import { needsRefresh, refreshAuth } from './auth';

const portal_endpoint = "https://portal-api.hotmic.io"; // [PROD]
// const portal_endpoint = "https://portal-test.api.hotmic.io"; // [Cloud9] - TODO change this back to Prod before deploy
// const portal_endpoint = "https://portal-dev.api.hotmic.io"; // [STAGING] - TODO change back to prod before deploy
// const portal_endpoint = "http://localhost:3001"; // [LOCAL] - Development server

let refreshPromise = null;

// Add list of public endpoints that don't require auth
const PUBLIC_ENDPOINTS = [
  'auth/login',
  'auth/oauth/login',
  'auth/password/reset',
  'admin/theme',  // Add the theme endpoint to public endpoints
  'users/me',     // Add the user refresh endpoint
];

// Helper function to check if current access token is still valid
const isCurrentTokenValid = () => {
  const expiresAt = localStorage.getItem('access_token_expires_at');
  if (!expiresAt) return false;
  
  const expiresAtTimestamp = parseInt(expiresAt, 10);
  const currentTime = Math.floor(Date.now() / 1000);
  
  return expiresAtTimestamp > currentTime;
};

export const fetchWrapperPortal = async ({ ...args }) => {
  try {
    // Check if endpoint requires auth
    const isPublicEndpoint = PUBLIC_ENDPOINTS.some(endpoint => args.url.startsWith(endpoint));

    // Only check for refresh if not a public endpoint and not a refresh request
    if (!isPublicEndpoint && args.url !== 'auth/refresh/' && needsRefresh()) {
      try {
        // Use a single refresh promise to prevent multiple simultaneous refresh attempts
        if (!refreshPromise) {
          refreshPromise = refreshAuth();
        }
        await refreshPromise;
        refreshPromise = null;
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
        // Clear refresh promise on error
        refreshPromise = null;
        
        // Only clear auth state and redirect if the current access token is also invalid
        // If current token is still valid, let the request proceed with the existing token
        if (!isCurrentTokenValid()) {
          console.error('Current access token is also expired, logging out');
          localStorage.clear();
          // Only redirect if we're not already heading to login
          if (!window.location.pathname.includes('/auth/login')) {
            window.location.href = '/auth/login';
          }
          throw refreshError;
        } else {
          console.warn('Token refresh failed but current token is still valid, continuing with existing token');
          // Don't throw the error, let the request proceed with the current valid token
        }
      }
    }

    const fetchObj = {};
    fetchObj.headers = args.headers || {};
    fetchObj.method = args.method.toUpperCase();
    fetchObj.body = args.body ? args.body : null;
    
    // Only set Content-Type for JSON requests, not for FormData
    // Let browser set the Content-Type with boundary for multipart/form-data
    if (!args.isFormData) {
      fetchObj.headers["Content-Type"] = "application/json";
    }
    
    fetchObj.headers["ApiKey"] = process.env.REACT_APP_APP_KEY;
    
    // Add auth header if token exists, even for public endpoints
    if (!fetchObj.headers.Authorization && args.addBearer) {
      const token = args.token || localStorage.getItem("access_token");
      if (token) {
        fetchObj.headers.Authorization = args.token ? args.token : `Bearer ${token}`;
      } else if (!isPublicEndpoint) {
        throw new Error('No authorization token available');
      }
    }

    if (args.signal) {
      fetchObj.signal = args.signal;
    }

    const result = await fetch(`${portal_endpoint}/${args.url}`, fetchObj);
    // Handle 401 responses only for non-public endpoints (403 is permission issue, not auth issue)
    if (!isPublicEndpoint && result.status === 401) {
      // Just log the error and throw - don't automatically logout
      // Let the calling code decide what to do
      console.warn(`401 Auth error for ${args.url} - token may be expired`);
      throw new Error(`Authentication error: ${result.status}`);
    }
    
    // Additional check for 403 errors on public endpoints
    if (isPublicEndpoint && result.status === 403) {
      throw new Error(`Forbidden: You don't have permission to access this resource (403)`);
    }

    // Check if response is JSON
    const contentType = result.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      console.error('Non-JSON response received:', {
        contentType,
        status: result.status,
        statusText: result.statusText
      });
      const text = await result.text();
      console.error('Response text:', text);
      throw new Error(`Expected JSON response but got ${contentType}`);
    }
    

    
    return result && result.status === 204 ? result.status : await result.json();
  } catch (e) {
    console.error(`HTTP ERROR:`, e);
    // Don't rethrow network errors or temporary auth errors
    if (e.name === 'TypeError' || e.name === 'NetworkError' || 
        (e.message && e.message.includes('Authentication error'))) {
      console.warn('Network/auth error occurred, might be temporary');
      return { error: e.message || 'Network error occurred' };
    }
    throw e;
  }
};
